// Enhanced video.js with seamless preview ↔ fullscreen transitions
import {
  setIsBuffering,
  setGlobalStreamType,
  setIsPlaying,
  previewToNotShow,
} from "../state";
import { appendAvAdParams } from "./utils/mediaTailoreHelper";

let playerInstance = null;
let shakaPlayer = null;
let reconnect_count = 0;
let stuckCount = 0;
let bufferingTimer = null;
let seekTimeoutTimer = null;

let reconnect_timer = null;
const MAX_RETRIES = 5;
let currentUrl = "";
let context = "";
let seeking = false;
let lastKnownTime = 0;
let monitorTimer = null;
let isBuffering = false;
let playerReady = false;
let playerState = "IDLE";
let lastSeekTime = 0;
const SEEK_THROTTLE_MS = 800;

// Enhanced seeking state management
let lastSuccessfulSeekTime = 0;
let seekingStartTime = 0;
let consecutiveStuckCount = 0;
const MAX_STUCK_COUNT = 3;
const SEEK_TIMEOUT_MS = 10000;
const STUCK_THRESHOLD_MS = 100;

// Multi-mode support
let currentMode = "web"; // "web", "preview", "fullscreen", "exoplayer"

// Make reconnection info available globally
if (!window.reconnectInfo) {
  window.reconnectInfo = {
    count: reconnect_count,
    max: MAX_RETRIES,
  };
}

// Check if we're running in Android WebView
function isAndroidWebView() {
  const hasAndroidBridge = window.AndroidVideoPlayer !== undefined;
  console.log("🔍 Android WebView check:", hasAndroidBridge);
  return hasAndroidBridge;
}

// Check if URL should be handled by ExoPlayer
function shouldUseExoPlayer(url) {
  if (!isAndroidWebView()) {
    console.log("❌ Not Android WebView, using web player");
    return false;
  }

  console.log("🔍 Checking URL for ExoPlayer:", url);
  const shouldUse =
    url &&
    (url.includes(".ts") ||
      url.includes(".m3u8") ||
      url.includes("live") ||
      url.includes("proxy_url") ||
      url.includes("/live/") ||
      url.includes("movie") ||
      url.includes("stream") ||
      url.startsWith("http"));

  console.log("📱 Should use ExoPlayer:", shouldUse);
  return shouldUse;
}

// Get current page context to determine playback mode
function getCurrentContext() {
  const currentPath = window.location.hash || window.location.pathname;
  console.log("🔍 Current path for context:", currentPath);

  if (currentPath.includes("livetv")) {
    console.log("📺 Context: LiveTV fullscreen");
    return "fullscreen";
  } else if (currentPath.includes("channel-menu")) {
    console.log("📺 Context: Channel menu preview");
    return "preview";
  } else {
    console.log("📺 Context: Background");
    return "background";
  }
}

// Setup global Android communication
function setupAndroidCommunication() {
  if (!isAndroidWebView()) return;

  console.log("📱 Setting up Android communication bridge");

  // Global state update functions for Android to call
  window.setIsBuffering = setIsBuffering;
  window.setIsPlaying = setIsPlaying;
  window.setGlobalStreamType = setGlobalStreamType;

  // Enhanced video events for Android callbacks
  if (!window.androidVideoEvents) {
    window.androidVideoEvents = {
      onVideoReady: () => {
        console.log("📱 Android video ready callback received");
        playerReady = true;
        playerState = "PLAYING";
        currentMode = "exoplayer";

        const currentContext = getCurrentContext();
        console.log("📺 Video ready in context:", currentContext);

        if (currentContext === "fullscreen") {
          console.log("🎯 LiveTV video ready - enhanced state management");
          setIsBuffering(false);
          setIsPlaying(true);
          setGlobalStreamType("live");

          setTimeout(() => {
            console.log("🎯 Triggering OSD show for LiveTV");
            if (window.triggerOSDShow) {
              window.triggerOSDShow();
            }

            const osdEvent = new CustomEvent("forceOSDShow", {
              detail: { source: "androidVideoReady", context: currentContext },
            });
            window.dispatchEvent(osdEvent);

            const osdElements = document.querySelectorAll(
              '[data-osd="true"], .osd-container'
            );
            osdElements.forEach((el) => {
              el.style.visibility = "visible";
              el.style.opacity = "1";
              el.style.display = "block";
              el.style.zIndex = "1001";
            });

            console.log(
              "🎯 OSD visibility forced for",
              osdElements.length,
              "elements"
            );
          }, 300);
        } else {
          console.log("📱 Preview video ready - normal state management");
          setTimeout(() => {
            setIsBuffering(false);
            setIsPlaying(true);
            setGlobalStreamType("live");
          }, 100);
        }
      },

      onError: (error) => {
        console.error("📱 Android video error callback:", error);
        playerReady = false;
        playerState = "ERROR";
        setIsBuffering(false);
        setIsPlaying(false);
      },

      onBuffering: (buffering) => {
        console.log("📱 Android buffering callback:", buffering);
        isBuffering = buffering;
        setIsBuffering(buffering);
      },

      onPlayingChanged: (playing) => {
        console.log("📱 Android playing changed callback:", playing);
        setIsPlaying(playing);
        if (playing) {
          playerState = "PLAYING";

          const currentContext = getCurrentContext();
          if (currentContext === "fullscreen") {
            setTimeout(() => {
              if (window.triggerOSDShow) {
                window.triggerOSDShow();
              }
            }, 500);
          }
        } else {
          playerState = "PAUSED";
        }
      },
    };
  }

  // Set up global event listeners for OSD management
  if (!window.osdEventListenersSetup) {
    window.osdEventListenersSetup = true;

    window.addEventListener("forceOSDShow", (event) => {
      console.log("🎯 Custom OSD show event received:", event.detail);

      setTimeout(() => {
        const allOSD = document.querySelectorAll(
          '[style*="zIndex"], [data-osd]'
        );
        allOSD.forEach((el) => {
          if (el.style.zIndex && parseInt(el.style.zIndex) > 500) {
            el.style.visibility = "visible";
            el.style.opacity = "1";
            el.style.display = "block";
          }
        });
      }, 100);
    });

    window.addEventListener("androidVideoReady", (event) => {
      console.log("🎯 Android video ready event received:", event.detail);

      if (event.detail?.context === "fullscreen") {
        setTimeout(() => {
          if (window.triggerOSDShow) {
            window.triggerOSDShow();
          }
        }, 200);
      }
    });
  }

  console.log("✅ Android communication setup completed");
}

// Load Shaka Player if not already loaded
async function loadShakaPlayer() {
  if (window.shaka) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/shaka-player/4.3.8/shaka-player.compiled.js";
    script.onload = () => {
      console.log("✅ Shaka Player loaded successfully");
      if (window.shaka && window.shaka.polyfill) {
        window.shaka.polyfill.installAll();
      }
      resolve();
    };
    script.onerror = () => {
      console.error("❌ Failed to load Shaka Player");
      reject(new Error("Failed to load Shaka Player"));
    };
    document.head.appendChild(script);
  });
}

const videoPlayer = {
  async destroy() {
    try {
      console.log("🧹 Destroying video player, mode:", currentMode);

      stopMonitoring();
      playerReady = false;
      playerState = "IDLE";
      seeking = false;

      clearTimeout(seekTimeoutTimer);
      clearTimeout(bufferingTimer);

      if (
        currentMode === "exoplayer" &&
        isAndroidWebView() &&
        window.AndroidVideoPlayer
      ) {
        try {
          console.log("📱 Stopping Android ExoPlayer");
          window.AndroidVideoPlayer.stopVideo();
        } catch (e) {
          console.warn("⚠️ Android ExoPlayer stop failed:", e);
        }
      }

      if (shakaPlayer) {
        try {
          await shakaPlayer.destroy();
          shakaPlayer = null;
          console.log("✅ Shaka Player destroyed");
        } catch (e) {
          console.warn("⚠️ Shaka Player destroy failed:", e);
        }
      }

      if (playerInstance) {
        try {
          playerInstance.pause();
          playerInstance.removeAttribute("src");
          playerInstance.load();
        } catch (e) {
          console.warn("⚠️ Video cleanup failed during destroy:", e);
        }
      }

      reconnect_count = 0;
      consecutiveStuckCount = 0;
      window.reconnectInfo.count = 0;
      clearTimeout(reconnect_timer);
      setIsBuffering(false);
      setIsPlaying(false);

      if (playerInstance && playerInstance.parentNode) {
        playerInstance.parentNode.removeChild(playerInstance);
        playerInstance = null;
      }

      currentMode = "web";
      console.log("✅ Video player destroyed");
    } catch (e) {
      console.error("❌ Error destroying player:", e);
    }
  },

  async playVideo(streamUrl, playerContext) {
    console.log("🎥 playVideo called:");
    console.log("📺 URL:", streamUrl);
    console.log("📺 Context:", playerContext);
    console.log("📺 Current mode:", currentMode);

    setupAndroidCommunication();

    // CRITICAL: Always stop any existing playback first to prevent dual audio
    console.log("🛑 Stopping any existing playback before starting new stream");

    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      console.log("📱 Stopping existing ExoPlayer");
      try {
        window.AndroidVideoPlayer.stopVideo();
      } catch (e) {
        console.warn("⚠️ Error stopping ExoPlayer:", e);
      }
    }

    // Stop web player if it exists
    if (playerInstance) {
      try {
        console.log("🌐 Stopping existing web player");
        playerInstance.pause();
        playerInstance.src = "";
        playerInstance.load();
      } catch (e) {
        console.warn("⚠️ Error stopping web player:", e);
      }
    }

    // Stop Shaka player if it exists
    if (shakaPlayer) {
      try {
        console.log("🎯 Stopping existing Shaka player");
        await shakaPlayer.unload();
      } catch (e) {
        console.warn("⚠️ Error stopping Shaka player:", e);
      }
    }

    // Reset states
    playerReady = false;
    playerState = "IDLE";
    seeking = false;
    isBuffering = false;
    setIsBuffering(false);
    setIsPlaying(false);

    // TEMPORARILY DISABLE seamless transitions until dual playback is fixed
    // TODO: Re-enable this once we confirm single stream playback works
    /*
    // CRITICAL: Check if same stream is already playing
    if (
      currentUrl === streamUrl &&
      playerInstance &&
      playerReady &&
      (playerState === "PLAYING" || playerState === "READY")
    ) {
      console.log("🔄 Same URL already playing, switching mode instead of restarting");
  
      // If using ExoPlayer, just switch the display mode
      if (currentMode === "exoplayer") {
        const targetContext = playerContext || getCurrentContext();
        console.log("📱 ExoPlayer mode switch to:", targetContext);
  
        if (targetContext === "fullscreen") {
          this.setFullScreen();
        } else if (targetContext === "preview") {
          this.setPreviewScreen();
        }
  
        return Promise.resolve();
      }
  
      // For web player, also just switch display mode
      if (playerContext === "fullscreen") {
        this.setFullScreen();
      } else if (playerContext === "preview") {
        this.setPreviewScreen();
      }
  
      return Promise.resolve();
    }
    */

    currentUrl = streamUrl;
    context = playerContext || getCurrentContext();
    reconnect_count = 0;
    consecutiveStuckCount = 0;
    window.reconnectInfo.count = 0;
    playerReady = false;
    playerState = "IDLE";
    seeking = false;
    clearTimeout(reconnect_timer);
    clearTimeout(seekTimeoutTimer);

    console.log("📺 Final context determined:", context);
    console.log("🚀 Starting fresh playback for:", streamUrl);

    if (isAndroidWebView() && shouldUseExoPlayer(streamUrl)) {
      console.log(
        "🎯 Using Android ExoPlayer for:",
        streamUrl,
        "context:",
        context
      );
      return this.playWithExoPlayer(streamUrl, context);
    }

    console.log("🌐 Using web-based player for:", streamUrl);
    return this.playWithWebPlayer(streamUrl, context);
  },

  async playWithExoPlayer(streamUrl, playerContext) {
    console.log("✅ NEW video.js running - playWithExoPlayer() reached");

    console.log("📱 Playing with Android ExoPlayer:");
    console.log("📺 URL:", streamUrl);
    console.log("📺 Context:", playerContext);

    return new Promise((resolve, reject) => {
      try {
        setIsBuffering(true);
        isBuffering = true;
        playerState = "LOADING";
        currentMode = "exoplayer";

        this.destroyWebPlayer();

        if (window.AndroidVideoPlayer) {
          if (window.currentChannelInfo) {
            window.AndroidVideoPlayer.setChannelInfo(
              window.currentChannelInfo.name || "Movie Player",
              window.currentChannelInfo.number || "0"
            );
          }

          let finalContext = playerContext;
          if (!finalContext) {
            finalContext = getCurrentContext();
          }

          console.log("🎬 Calling ExoPlayer with context:", finalContext);

          // For movie playback, always use fullscreen and disable LiveTV mode
          // ✅ Decide LiveTV mode correctly (LiveTV page fullscreen = true)
          const path = window.location.hash || window.location.pathname || "";
          const isLiveTV =
            finalContext === "fullscreen" && path.includes("livetv");

          console.log("🎛️ LiveTV mode?", isLiveTV, { finalContext, path });

          window.AndroidVideoPlayer.enableLiveTVMode(isLiveTV);
          setGlobalStreamType(isLiveTV ? "live" : "movie");

          // Rest of your existing playWithExoPlayer code...
          let callbackHandled = false;
          let timeoutId = null;

          const handleSuccess = () => {
            if (callbackHandled) return;
            callbackHandled = true;

            console.log("✅ ExoPlayer movie playback confirmed successful");
            playerReady = true;
            playerState = "PLAYING";
            setIsPlaying(true);
            setGlobalStreamType("movie"); // Set to movie instead of live
            setIsBuffering(false);
            isBuffering = false;

            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }

            resolve();
          };

          const originalOnReady = window.androidVideoEvents?.onVideoReady;
          if (window.androidVideoEvents) {
            window.androidVideoEvents.onVideoReady = () => {
              console.log("📱 Android callback: Movie ready received");

              if (originalOnReady) {
                originalOnReady();
              }

              handleSuccess();
              window.androidVideoEvents.onVideoReady = originalOnReady;
            };
          }

          console.log("🚀 Initiating ExoPlayer movie playback");
          appendAvAdParams(streamUrl, window.currentChannelInfo).then((preparedUrl) => {
            console.log("✅ CHOKEPOINT BEFORE ANDROID CALL");
            console.log("✅ JS streamUrl:", streamUrl);
            console.log("✅ JS preparedUrl:", preparedUrl);

            window.AndroidVideoPlayer.playVideo(preparedUrl, finalContext);
          });

          timeoutId = setTimeout(() => {
            if (!callbackHandled) {
              console.log(
                "⏰ ExoPlayer timeout reached - using fallback success"
              );

              if (window.androidVideoEvents && originalOnReady) {
                window.androidVideoEvents.onVideoReady = originalOnReady;
              }

              handleSuccess();
            }
          }, 4000);
        } else {
          console.error("❌ AndroidVideoPlayer bridge not available");
          reject(new Error("Android video player not available"));
        }
      } catch (e) {
        console.error("❌ ExoPlayer movie initialization error:", e);
        currentMode = "web";
        reject(e);
      }
    });
  },

  async destroyWebPlayer() {
    console.log("🧹 Destroying web player components");

    if (shakaPlayer) {
      try {
        await shakaPlayer.destroy();
        shakaPlayer = null;
      } catch (e) {
        console.warn("⚠️ Shaka cleanup failed:", e);
      }
    }

    if (playerInstance) {
      try {
        playerInstance.pause();
        playerInstance.removeAttribute("src");
        playerInstance.load();
        if (playerInstance.parentNode) {
          playerInstance.parentNode.removeChild(playerInstance);
        }
        playerInstance = null;
      } catch (e) {
        console.warn("⚠️ Video element cleanup failed:", e);
      }
    }
  },

  async playWithWebPlayer(streamUrl, playerContext) {
    console.log("🌐 Using web-based player for:", streamUrl);
    currentMode = "web";

    try {
      try {
        await loadShakaPlayer();
        if (window.shaka) {
          return this.playWithShaka(streamUrl, playerContext);
        }
      } catch (e) {
        console.warn("⚠️ Shaka Player not available, using HTML5:", e);
      }

      return this.playWithHTML5(streamUrl, playerContext);
    } catch (e) {
      console.error("❌ Web player initialization error:", e);
      throw e;
    }
  },

  async playWithShaka(streamUrl, playerContext) {
    console.log("🎯 Using Shaka Player");

    return new Promise(async (resolve, reject) => {
      try {
        await this.destroyWebPlayer();

        const videoElem = document.createElement("video");
        videoElem.setAttribute("crossorigin", "anonymous");
        videoElem.setAttribute("playsinline", "true");
        videoElem.setAttribute("webkit-playsinline", "true");
        videoElem.autoplay = false;
        videoElem.muted = false;
        videoElem.controls = false;
        videoElem.preload = "none";

        this.styleVideoElement(videoElem, playerContext);

        document.body.appendChild(videoElem);
        playerInstance = videoElem;

        shakaPlayer = new shaka.Player(videoElem);
        this.configureShakaPlayer(shakaPlayer);
        this.setupVideoEventListeners(videoElem, resolve, reject);

        await shakaPlayer.load(streamUrl);
        playerReady = true;
        playerState = "READY";
        this.startPlayback();
      } catch (error) {
        console.warn("⚠️ Shaka Player failed, falling back to HTML5:", error);
        this.fallbackToDirectVideo(streamUrl, playerContext);
        resolve();
      }
    });
  },

  async playWithHTML5(streamUrl, playerContext) {
    console.log("🎯 Using HTML5 video");

    return new Promise((resolve, reject) => {
      try {
        this.destroyWebPlayer();

        const videoElem = document.createElement("video");
        this.styleVideoElement(videoElem, playerContext);
        this.setupVideoEventListeners(videoElem, resolve, reject);

        document.body.appendChild(videoElem);
        playerInstance = videoElem;

        videoElem.src = streamUrl;
        videoElem.load();

        setTimeout(() => {
          this.startPlayback();
        }, 500);
      } catch (e) {
        reject(e);
      }
    });
  },

  styleVideoElement(videoElem, playerContext) {
    videoElem.style.position = "absolute";
    videoElem.style.left = playerContext === "preview" ? "50%" : "0";
    videoElem.style.top = playerContext === "preview" ? "50%" : "0";
    videoElem.style.width = playerContext === "preview" ? "35vw" : "100%";
    videoElem.style.height = playerContext === "preview" ? "35vh" : "100%";
    videoElem.style.zIndex = playerContext === "preview" ? "1" : "-1";
    videoElem.style.transform =
      playerContext === "preview" ? "translate(26.5%, -88%)" : "";
    videoElem.style.objectFit = "contain";
  },

  configureShakaPlayer(player) {
    player.configure({
      streaming: {
        bufferBehind: 30,
        bufferingGoal: 10,
        rebufferingGoal: 2,
        bufferAhead: 15,
        maxDisabledTime: 30,
        segmentRequestTimeoutMs: 10000,
        retryParameters: {
          timeout: 15000,
          maxAttempts: 4,
          baseDelay: 500,
          backoffFactor: 1.5,
          fuzzFactor: 0.3,
        },
      },
    });
  },

  setupVideoEventListeners(videoElem, resolve, reject) {
    videoElem.addEventListener("loadstart", () => {
      setIsBuffering(true);
      isBuffering = true;
    });

    videoElem.addEventListener("loadedmetadata", () => {
      playerReady = true;
      playerState = "READY";
    });

    videoElem.addEventListener("canplay", () => {
      setIsBuffering(false);
      isBuffering = false;
      reconnect_count = 0;
      consecutiveStuckCount = 0;
      window.reconnectInfo.count = 0;
    });

    videoElem.addEventListener("playing", () => {
      playerState = "PLAYING";
      setIsPlaying(true);
      setGlobalStreamType("live");
      setIsBuffering(false);
      isBuffering = false;

      setTimeout(() => {
        startMonitoring();
      }, 3000);

      if (resolve) {
        resolve();
        resolve = null;
      }
    });

    videoElem.addEventListener("pause", () => {
      playerState = "PAUSED";
      setIsPlaying(false);
    });

    videoElem.addEventListener("waiting", () => {
      clearTimeout(bufferingTimer);
      bufferingTimer = setTimeout(() => {
        if (!videoElem.paused && videoElem.readyState < 3) {
          setIsBuffering(true);
          isBuffering = true;
        }
      }, 1000);
    });

    videoElem.addEventListener("ended", () => {
      playerState = "COMPLETED";
      playerReady = false;
    });

    videoElem.addEventListener("error", (event) => {
      const error = event.target.error;
      console.error("❌ Video error:", error);
      playerReady = false;
      playerState = "ERROR";

      if (reject) {
        reject(new Error(`Video error: ${error ? error.code : "unknown"}`));
        reject = null;
      }
    });
  },

  startPlayback() {
    if (currentMode === "exoplayer") {
      console.log("📱 ExoPlayer handles playback automatically");
      return;
    }

    if (playerInstance && playerReady) {
      playerInstance
        .play()
        .then(() => {
          console.log("✅ Playback started successfully");
        })
        .catch((error) => {
          console.error("❌ Play failed:", error);
          setTimeout(() => tryReconnectMain(), 3000);
        });
    }
  },

  fallbackToDirectVideo(streamUrl, playerContext) {
    console.log("🔄 Using direct video fallback for stream");

    if (shakaPlayer) {
      shakaPlayer
        .destroy()
        .catch((e) => console.warn("⚠️ Shaka cleanup failed:", e));
      shakaPlayer = null;
    }

    if (!playerInstance) return;

    playerInstance.setAttribute("preload", "none");
    playerInstance.crossOrigin = "anonymous";

    const onLoadedMetadata = () => {
      playerReady = true;
      playerState = "READY";
      console.log("✅ Direct video: Metadata loaded");
      this.startPlayback();
    };

    const onCanPlay = () => {
      setIsBuffering(false);
      isBuffering = false;
      console.log("✅ Direct video: Can start playing");
    };

    const onError = (event) => {
      const error = event.target.error;
      console.error("❌ Direct video error:", error);
      tryReconnectMain();
    };

    playerInstance.addEventListener("loadedmetadata", onLoadedMetadata, {
      once: true,
    });
    playerInstance.addEventListener("canplay", onCanPlay, { once: true });
    playerInstance.addEventListener("error", onError);

    playerInstance.src = streamUrl;
    playerInstance.load();
  },

  // ENHANCED: Unified setFullScreen for both Web and Android
  setFullScreen() {
    console.log("📺 setFullScreen called, mode:", currentMode);

    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      console.log("📱 ExoPlayer: Switching to fullscreen mode");
      try {
        window.AndroidVideoPlayer.enableLiveTVMode(true);
        // Don't restart playback, just switch mode
        const script = `
          if (window.AndroidVideoPlayer && window.AndroidVideoPlayer.switchToFullscreen) {
            window.AndroidVideoPlayer.switchToFullscreen();
          } else {
            window.AndroidVideoPlayer.playVideo('${currentUrl}', 'fullscreen');
          }
        `;
        // For now, use the existing method but we could enhance Android side
        window.AndroidVideoPlayer.playVideo(currentUrl, "fullscreen");
        context = "fullscreen";
      } catch (e) {
        console.error("❌ Error setting ExoPlayer fullscreen:", e);
      }
      return;
    }

    // Web player fullscreen
    if (!previewToNotShow() && playerInstance) {
      try {
        playerInstance.style.position = "absolute";
        playerInstance.style.left = "0";
        playerInstance.style.top = "0";
        playerInstance.style.width = "100%";
        playerInstance.style.height = "100%";
        playerInstance.style.zIndex = "-1";
        playerInstance.style.transform = "";
        context = "fullscreen";
        console.log("✅ Web player set to fullscreen");
      } catch (e) {
        console.error("❌ Error setting web player fullscreen:", e);
      }
    }
  },

  // ENHANCED: Unified setPreviewScreen for both Web and Android
  setPreviewScreen() {
    console.log("📺 setPreviewScreen called, mode:", currentMode);

    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      console.log("📱 ExoPlayer: Switching to preview mode");
      try {
        window.AndroidVideoPlayer.enableLiveTVMode(false);
        // Don't restart playback, just switch mode
        window.AndroidVideoPlayer.playVideo(currentUrl, "preview");
        context = "preview";
      } catch (e) {
        console.error("❌ Error setting ExoPlayer preview:", e);
      }
      return;
    }

    // Web player preview
    if (previewToNotShow() && playerInstance) {
      try {
        playerInstance.style.zIndex = "1";
        playerInstance.style.left = "50%";
        playerInstance.style.top = "50%";
        playerInstance.style.width = "35vw";
        playerInstance.style.height = "35vh";
        playerInstance.style.position = "absolute";
        playerInstance.style.transform = "translate(26.5%, -88%)";
        context = "preview";
        console.log("✅ Web player set to preview");
      } catch (e) {
        console.error("❌ Error setting web player preview:", e);
      }
    }
  },

  setChannelInfo(name, number) {
    console.log("📺 Setting channel info:", number, "-", name);

    if (isAndroidWebView() && window.AndroidVideoPlayer) {
      try {
        window.AndroidVideoPlayer.setChannelInfo(name, number);
      } catch (e) {
        console.error("❌ Error setting Android channel info:", e);
      }
    }

    window.currentChannelInfo = { name, number };
  },

  showOSD() {
    console.log("📺 showOSD called, mode:", currentMode);

    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      try {
        const isLiveTV =
          window.AndroidVideoPlayer.isLiveTVMode &&
          window.AndroidVideoPlayer.isLiveTVMode();
        if (!isLiveTV) {
          window.AndroidVideoPlayer.showOSD();
        } else {
          console.log("📺 LiveTV mode - using SolidJS OSD");
        }
      } catch (e) {
        console.error("❌ Error showing Android OSD:", e);
      }
    }
  },

  hideOSD() {
    console.log("📺 hideOSD called, mode:", currentMode);

    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      try {
        window.AndroidVideoPlayer.hideOSD();
      } catch (e) {
        console.error("❌ Error hiding Android OSD:", e);
      }
    }
  },

  // ENHANCED: Simplified goToFullScreen using setFullScreen
  goToFullScreen() {
    console.log("goToFullScreen called, mode:", currentMode);

    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      try {
        console.log("ExoPlayer: Switching to fullscreen mode");

        // CRITICAL: Set LiveTV mode and force fullscreen transition
        window.AndroidVideoPlayer.enableLiveTVMode(true);

        // Force the switch to fullscreen display
        if (window.AndroidVideoPlayer.switchToFullscreen) {
          console.log("Using switchToFullscreen method");
          window.AndroidVideoPlayer.switchToFullscreen();
        } else {
          console.log(
            "switchToFullscreen not available, forcing fullscreen via playVideo"
          );
          // Fallback: Force fullscreen by calling playVideo with fullscreen context
          window.AndroidVideoPlayer.playVideo(currentUrl, "fullscreen");
        }

        // Update internal context
        context = "fullscreen";
      } catch (e) {
        console.error("Error setting ExoPlayer fullscreen:", e);
      }
      return;
    }

    // Web player fullscreen code (unchanged)
    if (!previewToNotShow() && playerInstance) {
      try {
        playerInstance.style.position = "absolute";
        playerInstance.style.left = "0";
        playerInstance.style.top = "0";
        playerInstance.style.width = "100%";
        playerInstance.style.height = "100%";
        playerInstance.style.zIndex = "-1";
        playerInstance.style.transform = "";
        context = "fullscreen";
        console.log("Web player set to fullscreen");
      } catch (e) {
        console.error("Error setting web player fullscreen:", e);
      }
    }
  },

  goToPreview() {
    console.log("goToPreview called, mode:", currentMode);

    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      try {
        console.log("ExoPlayer: Switching to preview mode");
        window.AndroidVideoPlayer.enableLiveTVMode(false);

        // Use the new switch method instead of restarting playback
        if (window.AndroidVideoPlayer.switchToPreview) {
          window.AndroidVideoPlayer.switchToPreview();
        } else {
          console.log(
            "switchToPreview not available, using enableLiveTVMode only"
          );
        }
      } catch (e) {
        console.error("Error setting ExoPlayer preview:", e);
      }
      return;
    }

    // Web player preview code (unchanged)
    if (previewToNotShow() && playerInstance) {
      try {
        playerInstance.style.zIndex = "1";
        playerInstance.style.left = "50%";
        playerInstance.style.top = "50%";
        playerInstance.style.width = "35vw";
        playerInstance.style.height = "35vh";
        playerInstance.style.position = "absolute";
        playerInstance.style.transform = "translate(26.5%, -88%)";
        context = "preview";
        console.log("Web player set to preview");
      } catch (e) {
        console.error("Error setting web player preview:", e);
      }
    }
  },

  getDuration() {
    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      try {
        return window.AndroidVideoPlayer.getDuration();
      } catch (e) {
        console.error("❌ Error getting ExoPlayer duration:", e);
        throw new Error("Unable to get duration from ExoPlayer");
      }
    }

    if (!playerInstance || playerState === "IDLE" || playerState === "ERROR") {
      throw new Error("Video player is not ready - state: " + playerState);
    }

    try {
      const duration = playerInstance.duration * 1000;
      if (!isFinite(duration) || duration <= 0) {
        throw new Error("Invalid duration value: " + duration);
      }
      return duration;
    } catch (e) {
      console.error("❌ Error getting duration:", e);
      throw e;
    }
  },

  getCurrentTime() {
    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      try {
        return window.AndroidVideoPlayer.getCurrentPosition();
      } catch (e) {
        console.error("❌ Error getting ExoPlayer current time:", e);
        throw new Error("Unable to get current time from ExoPlayer");
      }
    }

    if (!playerInstance || playerState === "IDLE" || playerState === "ERROR") {
      throw new Error("Video player is not ready - state: " + playerState);
    }

    try {
      const time = playerInstance.currentTime * 1000;
      if (!isFinite(time) || time < 0) {
        throw new Error("Invalid current time value: " + time);
      }
      return time;
    } catch (e) {
      console.error("❌ Error getting current time:", e);
      throw e;
    }
  },

  seek(position) {
    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      return new Promise((resolve, reject) => {
        try {
          window.AndroidVideoPlayer.seekTo(position);
          resolve(position);
        } catch (e) {
          reject(e);
        }
      });
    }

    return new Promise((resolve, reject) => {
      if (!playerInstance) {
        reject(new Error("Video player is not initialized"));
        return;
      }

      if (playerState !== "PLAYING" && playerState !== "PAUSED") {
        reject(new Error(`Cannot seek - invalid player state: ${playerState}`));
        return;
      }

      if (seeking) {
        reject(new Error("Seek already in progress"));
        return;
      }

      const seekTimeInSeconds = position / 1000;
      seeking = true;
      playerState = "SEEKING";

      const onSeeked = () => {
        playerInstance.removeEventListener("seeked", onSeeked);
        seeking = false;
        playerState = "PLAYING";
        resolve(playerInstance.currentTime * 1000);
      };

      playerInstance.addEventListener("seeked", onSeeked);
      playerInstance.currentTime = seekTimeInSeconds;
    });
  },

  pause() {
    console.log("⏸️ Pause called, mode:", currentMode);

    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      try {
        window.AndroidVideoPlayer.pauseVideo();
        setIsPlaying(false);
      } catch (e) {
        console.error("❌ Error pausing ExoPlayer:", e);
      }
      return;
    }

    try {
      if (playerInstance && playerState === "PLAYING" && !seeking) {
        playerInstance.pause();
        playerState = "PAUSED";
        setIsPlaying(false);
      }
    } catch (e) {
      console.error("❌ Error pausing:", e);
    }
  },

  resume() {
    console.log("▶️ Resume called, mode:", currentMode);

    if (
      currentMode === "exoplayer" &&
      isAndroidWebView() &&
      window.AndroidVideoPlayer
    ) {
      try {
        window.AndroidVideoPlayer.resumeVideo();
        setIsPlaying(true);
      } catch (e) {
        console.error("❌ Error resuming ExoPlayer:", e);
      }
      return;
    }

    try {
      if (playerInstance && playerState === "PAUSED" && !seeking) {
        playerInstance.play();
        playerState = "PLAYING";
        setIsPlaying(true);
      }
    } catch (e) {
      console.error("❌ Error resuming:", e);
    }
  },

  isReady() {
    if (currentMode === "exoplayer") {
      return playerReady && !seeking && !isBuffering;
    }

    return (
      playerReady &&
      !seeking &&
      !isBuffering &&
      (playerState === "PLAYING" || playerState === "PAUSED")
    );
  },

  getState() {
    return {
      ready: playerReady,
      seeking: seeking,
      buffering: isBuffering,
      state: playerState,
      consecutiveStuckCount: consecutiveStuckCount,
      lastSuccessfulSeekTime: lastSuccessfulSeekTime,
      usingShaka: !!shakaPlayer,
      usingExoPlayer: currentMode === "exoplayer",
      currentMode: currentMode,
      androidWebView: isAndroidWebView(),
      currentUrl: currentUrl,
      context: context,
    };
  },
};

// Enhanced monitoring for web players
function startMonitoring() {
  if (currentMode === "exoplayer") {
    console.log("📱 ExoPlayer monitoring handled by Android");
    return;
  }

  clearInterval(monitorTimer);
  console.log("📊 Starting web player monitoring");

  monitorTimer = setInterval(() => {
    if (
      isBuffering ||
      seeking ||
      playerState !== "PLAYING" ||
      !playerInstance
    ) {
      return;
    }

    try {
      const currentTime = playerInstance.currentTime * 1000;
      const timeDifference = Math.abs(currentTime - lastKnownTime);

      if (timeDifference > 2000) {
        consecutiveStuckCount = 0;
      }

      if (timeDifference < STUCK_THRESHOLD_MS) {
        consecutiveStuckCount++;

        if (consecutiveStuckCount >= MAX_STUCK_COUNT) {
          const timeSinceLastSeek = Date.now() - lastSeekTime;
          if (timeSinceLastSeek > 5000) {
            console.error("❌ Stream confirmed stuck - attempting reconnect");
            tryReconnectMain();
          } else {
            consecutiveStuckCount = Math.max(0, consecutiveStuckCount - 1);
          }
        }
      } else {
        lastKnownTime = currentTime;
        consecutiveStuckCount = 0;
      }
    } catch (e) {
      if (
        !e.message.includes("not ready") &&
        !e.message.includes("INVALID_STATE")
      ) {
        console.error("❌ Monitoring error:", e.message);
      }
    }
  }, 3000);
}

function stopMonitoring() {
  if (monitorTimer) {
    clearInterval(monitorTimer);
    monitorTimer = null;
    console.log("🛑 Stopped web player monitoring");
  }
}

async function tryReconnectMain() {
  if (currentMode === "exoplayer") {
    console.log("📱 ExoPlayer reconnection handled by Android");
    return;
  }

  clearTimeout(reconnect_timer);
  reconnect_count++;
  window.reconnectInfo.count = reconnect_count;
  playerReady = false;
  playerState = "RECONNECTING";
  seeking = false;
  consecutiveStuckCount = 0;

  if (reconnect_count > MAX_RETRIES) {
    console.log("❌ Maximum retry attempts reached");
    setIsBuffering(false);
    playerState = "ERROR";
    return;
  }

  console.log(`🔄 Reconnection attempt ${reconnect_count}/${MAX_RETRIES}`);
  setIsBuffering(true);

  const delay = Math.min(1000 * Math.pow(2, reconnect_count - 1), 8000);

  reconnect_timer = setTimeout(async () => {
    try {
      stopMonitoring();

      if (currentUrl) {
        console.log("🔄 Restarting stream:", currentUrl);
        try {
          await videoPlayer.playVideo(currentUrl, context);
          console.log("✅ Reconnection successful");
          reconnect_count = 0;
          window.reconnectInfo.count = 0;
        } catch (error) {
          console.error("❌ Reconnection failed:", error);
          setTimeout(() => tryReconnectMain(), 3000);
        }
      }
    } catch (e) {
      console.error("❌ Error during reconnection:", e);
      setTimeout(() => tryReconnectMain(), 3000);
    }
  }, delay);
}

// Initialize Android communication on load
if (typeof window !== "undefined") {
  setupAndroidCommunication();
}

export default videoPlayer;
